import { useEffect, useRef, type FormEvent } from 'react';
import type { EntryFormState } from './entryForm';

interface TimelineEntryFormProps {
  form: EntryFormState;
  onChange: (form: EntryFormState) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  /** "Add" for a new entry, "Save" for an edit. */
  submitLabel?: string;
  testId?: string;
}

const INPUT =
  'w-full min-h-11 sm:min-h-0 px-2.5 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * One entry's fields, for adding and editing alike. The title takes focus;
 * Enter in any single-line field saves and Escape anywhere cancels.
 */
export function TimelineEntryForm({
  form,
  onChange,
  onSave,
  onCancel,
  saving,
  submitLabel = 'Save',
  testId,
}: TimelineEntryFormProps) {
  const titleRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const set = (key: keyof EntryFormState) => (value: string) => onChange({ ...form, [key]: value });
  const canSave = !saving && form.title.trim().length > 0;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (canSave) onSave();
  };

  return (
    <form
      onSubmit={submit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel();
        }
      }}
      className="my-2 rounded-lg border border-primary-200 bg-primary-50/40 p-3 space-y-2"
      data-testid={testId}
    >
      <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr] gap-2">
        <label className="block">
          <span className="sr-only">Time</span>
          <input
            type="time"
            value={form.time}
            onChange={(e) => set('time')(e.target.value)}
            className={INPUT}
            data-testid="entry-time-input"
          />
        </label>
        <label className="block">
          <span className="sr-only">What happens</span>
          <input
            ref={titleRef}
            type="text"
            value={form.title}
            onChange={(e) => set('title')(e.target.value)}
            placeholder="What happens — e.g. First dance"
            maxLength={500}
            className={INPUT}
            data-testid="entry-title-input"
          />
        </label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="sr-only">Location</span>
          <input
            type="text"
            value={form.location}
            onChange={(e) => set('location')(e.target.value)}
            placeholder="Where (optional)"
            maxLength={500}
            className={INPUT}
            data-testid="entry-location-input"
          />
        </label>
        <label className="block">
          <span className="sr-only">Vendor note</span>
          <input
            type="text"
            value={form.vendorNote}
            onChange={(e) => set('vendorNote')(e.target.value)}
            placeholder="Vendor note (optional)"
            maxLength={500}
            className={INPUT}
            data-testid="entry-vendor-note-input"
          />
        </label>
      </div>
      <label className="block">
        <span className="sr-only">Notes</span>
        <textarea
          value={form.notes}
          onChange={(e) => set('notes')(e.target.value)}
          placeholder="Notes for the team (optional)"
          maxLength={2000}
          rows={2}
          className={`${INPUT} resize-y`}
          data-testid="entry-notes-input"
        />
      </label>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!canSave}
          className="min-h-11 sm:min-h-0 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
          data-testid="entry-save-button"
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-11 sm:min-h-0 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          data-testid="entry-cancel-button"
        >
          Cancel
        </button>
        <span className="hidden sm:inline ml-auto text-xs text-gray-400">
          Enter to save · Esc to cancel
        </span>
      </div>
    </form>
  );
}
